import type { Distance, Percentage } from "@pdfking/math"
import { ZERO_DISTANCE, ZERO_MARGINS, ZERO_SPATIAL_RECTANGLE } from "@pdfking/math/constants"
import { GroupMatrix, MatrixCompose, MatrixIdentity } from "@pdfking/renderer-api"
import { RecordMerge } from "@pdfking/tfunc"

import type { MediaHook } from "#internal"

/**
 * Bleed for a file that has none, made of the page itself: the page, and round
 * it the page turned over across whatever lies between — an edge for a strip,
 * both edges for a corner — each cut to its own piece, so what lies just
 * inside the box lies just outside too. Every piece is a column and a row of
 * the three the box and its bleed make; one whose bleed is nothing is left out.
 */
export const AutobleedProcessor: MediaHook = function* (media) {
  const { x, y, width, height } = media.position ?? ZERO_SPATIAL_RECTANGLE
  const { top, right, bottom, left } = media.mask ?? ZERO_MARGINS
  const own = media.media_matrix ?? MatrixIdentity()
  const across = -width as Distance
  const down = -height as Distance

  // The box itself, then each side of it: whether it is there, the line the
  // page turns over across, and the mask that keeps that side alone.
  const columns = [
    { there: true, x, scale: 1, mask: { left: ZERO_DISTANCE, right: ZERO_DISTANCE } },
    { there: left > 0, x, scale: -1, mask: { left, right: across } },
    { there: right > 0, x: (x + width) as Distance, scale: -1, mask: { left: across, right } },
  ]
  const rows = [
    { there: true, y, scale: 1, mask: { top: ZERO_DISTANCE, bottom: ZERO_DISTANCE } },
    { there: top > 0, y: (y + height) as Distance, scale: -1, mask: { top, bottom: down } },
    { there: bottom > 0, y, scale: -1, mask: { top: down, bottom } },
  ]

  for (const column of columns) {
    for (const row of rows) {
      if (column.there && row.there)
        yield {
          ...media,
          mask: RecordMerge(column.mask, row.mask),
          media_matrix: MatrixCompose(
            own,
            GroupMatrix({
              origin: { x: column.x, y: row.y },
              scale: { x: column.scale as Percentage, y: row.scale as Percentage },
            })
          ),
        }
    }
  }
}
